import { AdvancedSelect, Input } from '@orobix/orobix-fe-components'
import i18n from 'i18next'
import { useEffect, useState } from 'react'

import { FilterButton } from '../../../components/FilterButton/FilterButton'
import { FilteringValue } from '../../../types/filtering'

type FilterByRangeProps = {
  /** Used to derive unique input ids/names, e.g. the column key (`height`). */
  id: string
  minValue: FilteringValue
  maxValue: FilteringValue
  onFilterMin: (value: FilteringValue) => void
  onFilterMax: (value: FilteringValue) => void
  /** Input type, e.g. `'number'` (default) for dimensions or `'date'` for
   * the last-machined/first-seen date ranges. */
  type?: 'number' | 'date'
  /** When given, an "Only NaN / Exclude NaN" select is rendered below the
   * min/max inputs, in the same popover (used by the dimension columns). */
  nan?: {
    value: FilteringValue
    onFilter: (value: FilteringValue) => void
  }
}

type NanOption = { label: string; value: 'true' | 'false' }

// Typed input is kept in local draft state and only forwarded to
// `onFilterMin`/`onFilterMax` on blur or Enter — those callbacks trigger a
// `history.push` (via `useQueryParamFiltering`) and, in turn, a new fetch, so
// calling them on every keystroke would fire a request (and push a browser
// history entry) per character typed.
export const FilterByRange = ({
  id,
  minValue,
  maxValue,
  onFilterMin,
  onFilterMax,
  type = 'number',
  nan,
}: FilterByRangeProps) => {
  const [minDraft, setMinDraft] = useState(minValue ?? '')
  const [maxDraft, setMaxDraft] = useState(maxValue ?? '')

  // Keep the draft in sync when the committed value changes from outside
  // (e.g. cleared elsewhere, or restored from the URL on navigation).
  useEffect(() => setMinDraft(minValue ?? ''), [minValue])
  useEffect(() => setMaxDraft(maxValue ?? ''), [maxValue])

  const commitMin = () => onFilterMin(minDraft || undefined)
  const commitMax = () => onFilterMax(maxDraft || undefined)

  const nanOptions: NanOption[] = [
    { label: i18n.t('Only NaN'), value: 'true' },
    { label: i18n.t('Exclude NaN'), value: 'false' },
  ]

  return (
    <FilterButton
      filter={minValue || maxValue || nan?.value}
      onFilter={() => undefined /* individual fields drive state below */}
      renderFilter={() => (
        <>
          <Input
            name={`${id}-min`}
            id={`${id}-min`}
            type={type}
            placeholder={i18n.t('Min')}
            value={minDraft}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMinDraft(e.target.value)
            }
            onBlur={commitMin}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') commitMin()
            }}
          />
          <Input
            name={`${id}-max`}
            id={`${id}-max`}
            type={type}
            placeholder={i18n.t('Max')}
            value={maxDraft}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMaxDraft(e.target.value)
            }
            onBlur={commitMax}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') commitMax()
            }}
          />
          {nan && (
            <AdvancedSelect
              name={`${id}-nan`}
              id={`${id}-nan`}
              value={nanOptions.find((o) => o.value === nan.value)}
              options={nanOptions}
              onChange={(option) => nan.onFilter(option?.value)}
              isClearable
              placeholder={<>{i18n.t('Filter...')}</>}
            />
          )}
        </>
      )}
    />
  )
}
